/** Tenant settings key: `settings.libraryCardPrint` — controls physical card PDF/print layout. */

export interface LibraryCardPrintCustomField {
  label: string;
  /** Path under user registration metadata `formData` (e.g. "programme", "department"). */
  formDataKey: string;
}

export interface LibraryCardPrintSettings {
  /** Show tenant library full name (from Branding) in the card header. */
  showLibraryFullName: boolean;
  /** Show address (Branding) or institution subtitle line under the name. */
  showAddressOrSubtitle: boolean;
  /** Show uploaded stamp image (Setup → General policies card assets), if present. */
  showStampImage: boolean;
  /** Show uploaded signature image, if present. */
  showSignatureImage: boolean;
  /**
   * Optional second line under the library name on the card when branding address is empty.
   * Prefer setting address under Setup → Branding.
   */
  institutionSubtitle: string;
  /** Title shown on the colored banner before patron type (e.g. "ADMISSION TICKET"). */
  admissionBannerPrefix: string;
  showProfilePhoto: boolean;
  showPatronTypeBanner: boolean;
  showCardNumber: boolean;
  showEmail: boolean;
  showPhone: boolean;
  showRegistrationDate: boolean;
  /** Blank rule line for session / cohort. */
  showSessionLine: boolean;
  sessionLineLabel: string;
  showLibrarianSigLine: boolean;
  librarianSigLabel: string;
  customFormFields: LibraryCardPrintCustomField[];
  cardsPerRow: number;
  rowsPerPage: number;
}

export const DEFAULT_LIBRARY_CARD_PRINT_SETTINGS: LibraryCardPrintSettings = {
  showLibraryFullName: true,
  showAddressOrSubtitle: true,
  showStampImage: true,
  showSignatureImage: true,
  institutionSubtitle: "",
  admissionBannerPrefix: "ADMISSION TICKET",
  showProfilePhoto: true,
  showPatronTypeBanner: true,
  showCardNumber: true,
  showEmail: false,
  showPhone: false,
  showRegistrationDate: true,
  showSessionLine: false,
  sessionLineLabel: "Session",
  showLibrarianSigLine: true,
  librarianSigLabel: "Librarian's Signature & Stamp",
  customFormFields: [],
  /** Two columns × four rows = 8 ID-1 cards per A4 sheet (standard bulk print). */
  cardsPerRow: 2,
  rowsPerPage: 4,
};

export function mergeLibraryCardPrintSettings(raw: unknown): LibraryCardPrintSettings {
  const r = raw && typeof raw === "object" ? (raw as Partial<LibraryCardPrintSettings>) : {};
  const customRaw = r.customFormFields;
  const customFormFields: LibraryCardPrintCustomField[] = Array.isArray(customRaw)
    ? customRaw
        .filter(
          (x): x is LibraryCardPrintCustomField =>
            !!x &&
            typeof x === "object" &&
            typeof (x as any).label === "string" &&
            typeof (x as any).formDataKey === "string"
        )
        .map((x) => ({
          label: String((x as LibraryCardPrintCustomField).label).slice(0, 80),
          formDataKey: String((x as LibraryCardPrintCustomField).formDataKey).slice(0, 120),
        }))
    : DEFAULT_LIBRARY_CARD_PRINT_SETTINGS.customFormFields;
  const merged: LibraryCardPrintSettings = {
    ...DEFAULT_LIBRARY_CARD_PRINT_SETTINGS,
    ...r,
    showLibraryFullName: r.showLibraryFullName !== false,
    showAddressOrSubtitle: r.showAddressOrSubtitle !== false,
    showStampImage: r.showStampImage !== false,
    showSignatureImage: r.showSignatureImage !== false,
    customFormFields,
    cardsPerRow: clampInt(r.cardsPerRow, 1, 2, DEFAULT_LIBRARY_CARD_PRINT_SETTINGS.cardsPerRow),
    rowsPerPage: clampInt(r.rowsPerPage, 1, 4, DEFAULT_LIBRARY_CARD_PRINT_SETTINGS.rowsPerPage),
  };
  delete (merged as unknown as { patronTypeBandColors?: unknown }).patronTypeBandColors;
  delete (merged as unknown as { defaultBandColor?: unknown }).defaultBandColor;
  delete (merged as unknown as { showSeeOver?: unknown }).showSeeOver;
  return merged;
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function readFormDataValue(
  formData: Record<string, unknown> | undefined,
  key: string
): string {
  if (!formData || !key) return "";
  const v = formData[key];
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}
